import React, { useEffect, useMemo, useState } from 'react';

import { useCurrentTier } from '@/hooks/useCurrentTier';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import {
  useUpsertUserConfiguredUsageLimits,
  useUserConfiguredUsageLimits,
} from '@/hooks/useSwr';
import { PaymentPlanTier } from '@/types/schema';

import { getCostForTraces } from './getCostForTraces';

export const parseLimit = (limit: string | null) => {
  if (!limit || limit === '') {
    return null;
  }
  const parsed = parseFloat(limit);
  if (!isNaN(parsed)) {
    if (parsed < 0) {
      return null;
    }
    return parsed;
  } else {
    return null;
  }
};

export const getLimitError = (limit: string) => {
  if (limit === '') {
    return null;
  }
  const parsed = parseFloat(limit);
  if (isNaN(parsed)) {
    return 'Please enter a valid number';
  } else if (parsed < 0) {
    return 'You must enter a positive number';
  } else {
    return null;
  }
};

type useConfigureUsageLimitReturn = {
  upsertUserDefinedLimit: ReturnType<typeof useUpsertUserConfiguredUsageLimits>;
  totalLimitError: string | null;
  longlivedLimitError: string | null;
  isLoading: boolean;
  isMutating: boolean;
  maxCostEstimate: number | null;
  totalLimitText: string;
  longlivedLimitText: string;
  setTotalLimitText: React.Dispatch<React.SetStateAction<string>>;
  setLonglivedLimitText: React.Dispatch<React.SetStateAction<string>>;
  pricePerTrace: number;
  saveLimits: () => void;
  disableSave: boolean;
  reset: () => void;
};

export const useConfigureUsageLimit = ({
  setupTier,
  workspaceId,
}: {
  setupTier?: PaymentPlanTier;
  workspaceId?: string;
}): useConfigureUsageLimitReturn => {
  const { isLegacy, tier: curentTier } = useCurrentTier();
  const showTTLUI = useInstanceFlag(InstanceFlags.show_ttl_ui);
  const usageLimitQuery = useUserConfiguredUsageLimits({
    skip: isLegacy,
    workspaceId,
  });

  const upsertUserDefinedLimit = useUpsertUserConfiguredUsageLimits({
    skipTenantHeaders: !!workspaceId,
  });

  const currentTier = setupTier ?? curentTier;

  const totalLimit = usageLimitQuery.data?.find(
    (s) => s.limit_type === 'monthly_traces'
  )?.limit_value;

  const longlivedLimit = usageLimitQuery.data?.find(
    (s) => s.limit_type === 'monthly_longlived_traces'
  )?.limit_value;

  const [totalLimitText, setTotalLimitText] = useState<string>('');

  const [longlivedLimitText, setLonglivedLimitText] = useState<string>('');

  useEffect(() => {
    if (!upsertUserDefinedLimit.isMutating && !usageLimitQuery.isValidating) {
      if (totalLimit) {
        setTotalLimitText(`${totalLimit}`);
      }
      if (longlivedLimit) {
        setLonglivedLimitText(`${longlivedLimit}`);
      }
    }
  }, [
    totalLimit,
    longlivedLimit,
    upsertUserDefinedLimit.isMutating,
    usageLimitQuery.isValidating,
  ]);

  const totalLimitNumber = useMemo(
    () => parseLimit(totalLimitText),
    [totalLimitText]
  );
  const longlivedLimitNumber = useMemo(
    () => parseLimit(longlivedLimitText),
    [longlivedLimitText]
  );

  const { maxCostEstimate, pricePerTrace } = useMemo(
    () => getCostForTraces(totalLimitNumber, longlivedLimitNumber, currentTier),
    [totalLimitNumber, longlivedLimitNumber, currentTier]
  );

  const totalLimitError = useMemo(
    () => getLimitError(totalLimitText),
    [totalLimitText]
  );

  const longlivedLimitError = useMemo(() => {
    if (
      longlivedLimitNumber !== null &&
      totalLimitNumber !== null &&
      longlivedLimitNumber > totalLimitNumber
    ) {
      return 'Long-lived trace limit cannot be greater than total trace limit';
    }
    return getLimitError(longlivedLimitText);
  }, [longlivedLimitText, totalLimitNumber, longlivedLimitNumber]);

  const saveLimits = async () => {
    if (longlivedLimitNumber !== null) {
      await upsertUserDefinedLimit.trigger({
        json: {
          limit_type: 'monthly_longlived_traces',
          limit_value: longlivedLimitNumber,
        },
        ...(workspaceId && {
          headers: { 'X-Tenant-id': workspaceId },
        }),
      });
    }
    if (totalLimitNumber !== null) {
      await upsertUserDefinedLimit.trigger({
        json: {
          limit_type: 'monthly_traces',
          limit_value: totalLimitNumber,
        },
        ...(workspaceId && {
          headers: { 'X-Tenant-id': workspaceId },
        }),
      });
    }
  };

  const reset = () => {
    setTotalLimitText(totalLimit ? `${totalLimit}` : '');
    setLonglivedLimitText(longlivedLimit ? `${longlivedLimit}` : '');
  };

  return {
    upsertUserDefinedLimit,
    maxCostEstimate,
    totalLimitText,
    longlivedLimitText,
    totalLimitError,
    longlivedLimitError,
    disableSave: showTTLUI
      ? !!totalLimitError || !!longlivedLimitError
      : !!totalLimitError,
    setTotalLimitText,
    setLonglivedLimitText,
    isLoading: usageLimitQuery.isLoading,
    isMutating: upsertUserDefinedLimit.isMutating,
    pricePerTrace,
    saveLimits,
    reset,
  };
};
